import React, { useEffect, useState } from 'react'
import { FaImages, FaTrash, FaDownload, FaVideo, FaFileAudio, FaFile } from 'react-icons/fa'
import axiosUserInstance from '../../axiosUserInstance';
import { deleteImage, downloadFile, getFileType, downloadMultipleFiles } from '../../utils/common';
import '../../Assets/css/LibraryTab.css';

const LibraryTab = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, totalPages: 1 });
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);

  useEffect(() => {
    fetchMediaLibrary(pagination.page);
  }, []);

  const fetchMediaLibrary = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axiosUserInstance.get(
        `/file/user-images`,
        { params: { page, limit: 20 } }
      );
      if (response?.status === 200) {
        const { data, pagination: paginationData } = response.data;
        setMedia(data || []);
        setPagination(paginationData);
        console.log("Media Library Data:", data);
      } else {
        console.log(
          "error ==>",
          response?.message ||
            "Media Library fetch failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      console.log("error ==>", apiError);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Are you sure you want to delete this media?")) {
      return;
    }

    setDeleteLoading(mediaId);
    try {
      await deleteImage(mediaId);
      setMedia(media.filter(item => item._id !== mediaId));
      console.log("Media deleted successfully");
    } catch (err) {
      console.error("Failed to delete media:", err);
      alert("Failed to delete media. Please try again.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleDownloadMedia = async (mediaUrl, fileName) => {
    setDownloadLoading(fileName);
    try {
      await downloadFile(mediaUrl, fileName);
    } catch (err) {
      console.error("Failed to download media:", err);
      alert("Failed to download media. Please try again.");
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!window.confirm(`Download all ${filteredMedia.length} files?`)) {
      return;
    }

    setDownloadAllLoading(true);
    try {
      await downloadMultipleFiles(filteredMedia);
      console.log("All files downloaded successfully");
    } catch (err) {
      console.error("Failed to download all files:", err);
      alert("Failed to download some files. Please try again.");
    } finally {
      setDownloadAllLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchMediaLibrary(newPage);
    }
  };

  const getMediaIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <FaImages />;
      case 'video':
        return <FaVideo />;
      case 'audio':
        return <FaFileAudio />;
      default:
        return <FaFile />;
    }
  };

  const renderMediaPreview = (media) => {
    const mediaType = getFileType(media.fileType);

    if (mediaType === 'image') {
      return (
        <img
          src={media.imageUrl}
          alt={media.fileName}
          className="media-preview"
        />
      );
    } else if (mediaType === 'video') {
      return (
        <div className="video-preview">
          <video src={media.imageUrl} className="media-preview" />
          <div className="video-icon-overlay">
            <FaVideo />
          </div>
        </div>
      );
    } else if (mediaType === 'audio') {
      return (
        <div className="audio-preview">
          <div className="audio-icon">
            <FaFileAudio />
          </div>
          <audio controls className="audio-player">
            <source src={media.imageUrl} type={media.fileType} />
          </audio>
        </div>
      );
    } else {
      return (
        <div className="file-preview">
          <FaFile className="file-icon" />
        </div>
      );
    }
  };

  const filteredMedia = filterType === 'all' 
    ? media 
    : media.filter(item => getFileType(item.fileType) === filterType);

  return (
    <div className="library-tab">
      <div className="library-header">
        <h2 className="section-title">
          <FaImages /> Media Library
        </h2>
        {media.length > 0 && (
          <button 
            className="download-all-btn"
            onClick={handleDownloadAll}
            disabled={downloadAllLoading || filteredMedia.length === 0}
          >
            {downloadAllLoading ? 'Downloading...' : `Download All (${filteredMedia.length})`}
          </button>
        )}
      </div>

      {media.length > 0 && (
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            All ({media.length})
          </button>
          <button
            className={`filter-tab ${filterType === 'image' ? 'active' : ''}`}
            onClick={() => setFilterType('image')}
          >
            Images ({media.filter(m => getFileType(m.fileType) === 'image').length})
          </button>
          <button
            className={`filter-tab ${filterType === 'video' ? 'active' : ''}`}
            onClick={() => setFilterType('video')}
          >
            Videos ({media.filter(m => getFileType(m.fileType) === 'video').length})
          </button>
          <button
            className={`filter-tab ${filterType === 'audio' ? 'active' : ''}`}
            onClick={() => setFilterType('audio')}
          >
            Audio ({media.filter(m => getFileType(m.fileType) === 'audio').length})
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <p>Loading media...</p>
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="empty-state">
          <p>{filterType === 'all' 
            ? 'No media uploaded yet. Start uploading your files!' 
            : `No ${filterType}s found.`}
          </p>
        </div>
      ) : (
        <>
          <div className="media-grid">
            {filteredMedia.map((mediaItem) => {
              const mediaType = getFileType(mediaItem.fileType);
              return (
                <div key={mediaItem._id} className={`media-card media-${mediaType}`}>
                  <div className="media-container">
                    {renderMediaPreview(mediaItem)}
                    <div className="media-overlay">
                      <button
                        className="download-btn"
                        onClick={() => handleDownloadMedia(mediaItem.imageUrl, mediaItem.fileName)}
                        disabled={downloadLoading === mediaItem.fileName}
                        title="Download media"
                      >
                        {downloadLoading === mediaItem.fileName ? '...' : <FaDownload />}
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteMedia(mediaItem._id)}
                        disabled={deleteLoading === mediaItem._id}
                        title="Delete media"
                      >
                        {deleteLoading === mediaItem._id ? '...' : <FaTrash />}
                      </button>
                    </div>
                    <div className="media-type-badge">
                      {getMediaIcon(mediaType)}
                    </div>
                  </div>
                  <div className="media-info">
                    <p className="file-name" title={mediaItem.fileName}>{mediaItem.fileName}</p>
                    <p className="upload-date">
                      {new Date(mediaItem.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default LibraryTab